import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  getBillingProfile,
  upsertBillingProfile,
  listMyInvoices,
  redeemCoupon,
} from "@/lib/billing/billing.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLang } from "@/hooks/use-language";
import { ManageSubscriptionButton } from "@/components/ManageSubscriptionButton";
import { useSubscription } from "@/hooks/useSubscription";
import { ExternalLink } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/billing")({
  component: BillingPage,
  head: () => ({ meta: [{ title: "Billing — Jozori" }] }),
});

function fmtMoney(cents: number, currency: string) {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(
    cents / 100,
  );
}

function BillingPage() {
  const { lang, dir } = useLang();
  const language = lang;
  const qc = useQueryClient();
  const getProfile = useServerFn(getBillingProfile);
  const saveProfile = useServerFn(upsertBillingProfile);
  const listInv = useServerFn(listMyInvoices);
  const redeem = useServerFn(redeemCoupon);

  const profileQ = useQuery({ queryKey: ["billing-profile"], queryFn: () => getProfile() });
  const invoicesQ = useQuery({ queryKey: ["my-invoices"], queryFn: () => listInv() });

  const [form, setForm] = useState<any>({});
  const merged = { ...(profileQ.data ?? {}), ...form };

  const saveM = useMutation({
    mutationFn: () => saveProfile({ data: merged }),
    onSuccess: () => {
      toast.success(language === "ar" ? "تم الحفظ" : "Saved");
      qc.invalidateQueries({ queryKey: ["billing-profile"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to save"),
  });

  const [code, setCode] = useState("");
  const redeemM = useMutation({
    mutationFn: () => redeem({ data: { code } }),
    onSuccess: (r: any) => {
      if (r.ok) toast.success(language === "ar" ? "تم تفعيل الكود" : "Code applied");
      else toast.error(r.error ?? "Invalid code");
    },
  });

  const t = (en: string, ar: string) => (language === "ar" ? ar : en);
  const { subscription } = useSubscription();

  return (
    <div dir={dir} className="mx-auto max-w-3xl px-4 py-6 space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("Billing", "الفواتير")}</h1>
        <Link to="/app/parent" className="text-sm text-muted-foreground hover:underline">
          {t("Back to dashboard", "العودة للوحة")}
        </Link>
      </header>

      {subscription?.isActive && subscription.tier !== "free" && (
        <Card>
          <CardHeader>
            <CardTitle>{t("Your subscription", "اشتراكك")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm">
                <p className="font-medium capitalize">{subscription.tier}</p>
                {subscription.currentPeriodEnd && (
                  <p className="text-muted-foreground text-xs mt-1">
                    {subscription.cancelAtPeriodEnd
                      ? t("Ends on ", "ينتهي في ")
                      : t("Renews on ", "يتجدد في ")}
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                  </p>
                )}
              </div>
              <ManageSubscriptionButton />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed border-t pt-3">
              {t(
                "Pause and cancel are equally available in the portal. Pausing keeps your family's shelf and your child's memory; cancelling ends access at the end of the current period.",
                "الإيقاف المؤقت والإلغاء متاحان بنفس السهولة في البوابة. الإيقاف يحفظ مكتبة عائلتك وذكريات طفلك؛ والإلغاء ينهي الوصول مع نهاية الفترة الحالية.",
              )}
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>{t("Billing details", "تفاصيل الفوترة")}</CardTitle></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          {[
            ["full_name", t("Full name", "الاسم الكامل")],
            ["email", t("Email", "البريد الإلكتروني")],
            ["company_name", t("Company (optional)", "الشركة (اختياري)")],
            ["vat_id", t("VAT / Tax ID (optional)", "الرقم الضريبي (اختياري)")],
            ["country", t("Country (ISO code, e.g. SA)", "الدولة (رمز ISO)")],
            ["city", t("City", "المدينة")],
            ["address_line1", t("Address line 1", "العنوان 1")],
            ["address_line2", t("Address line 2", "العنوان 2")],
            ["postal_code", t("Postal code", "الرمز البريدي")],
            ["region", t("Region / State", "المنطقة")],
          ].map(([key, label]) => (
            <div key={key} className="space-y-1">
              <Label>{label}</Label>
              <Input
                value={merged[key] ?? ""}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
            </div>
          ))}
          <div className="sm:col-span-2">
            <Button onClick={() => saveM.mutate()} disabled={saveM.isPending}>
              {saveM.isPending ? "…" : t("Save details", "حفظ التفاصيل")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{t("Have a promo code?", "لديك كود خصم؟")}</CardTitle></CardHeader>
        <CardContent className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="JOZORI25" />
          <Button onClick={() => redeemM.mutate()} disabled={!code || redeemM.isPending}>
            {t("Apply", "تطبيق")}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{t("Invoices & receipts", "الفواتير والإيصالات")}</CardTitle></CardHeader>
        <CardContent>
          {invoicesQ.isLoading ? (
            <p className="text-sm text-muted-foreground">{t("Loading…", "جاري التحميل…")}</p>
          ) : (invoicesQ.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("No invoices yet — they appear automatically after each payment.",
                 "لا توجد فواتير بعد — ستظهر تلقائيًا بعد كل عملية دفع.")}
            </p>
          ) : (
            <ul className="divide-y">
              {(invoicesQ.data ?? []).map((inv: any) => (
                <li key={inv.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium">{inv.invoice_number}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(inv.issued_at).toLocaleDateString()} · {inv.status}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{fmtMoney(inv.amount_cents, inv.currency)}</span>
                    {inv.hosted_invoice_url && (
                      <a
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        PDF <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
